import { takeEvery, put, call } from 'redux-saga/effects';
import { models } from 'powerbi-client';

import * as types from './dailyReportActionType';
import * as actions from './dailyReportActions';
import * as services from './dailyReportServices';

function* fetchDailyPowerBIReportSaga() {
    const environment = window.configuration.ENVIRONMENT || 'dev';
    const sharedReport = window.configuration.PowerBI_Reports?.dailyReports?.sharedReport;
    const environmentModelConfig = window.configuration.PowerBI_Reports?.dailyReports?.environmentModelConfig?.[environment];

    if (!sharedReport || !environmentModelConfig) {
        console.error('No Power BI report configured for environment:', environment);
        yield put(actions.fetchDailyPowerBIReportFailure('No Power BI report configured for this environment'));
        return;
    }

    const { reportId, reportWorkspaceId, goldenModelId } = sharedReport;
    const { modelId: targetModelId, modelWorkspaceId: targetModelWorkspaceId } = environmentModelConfig;

    try {
        const response = yield call(services.getDailyPowerBIToken);

        const embedPayload = {
            token: response?.access_token,
            reportId,
            workspaceId: reportWorkspaceId
        }
        const embedResponse = yield call(services.getDailyEmbedResponse, embedPayload);
        const tokenResponse = yield call(services.getDailyMultiResourceTokenResponse, {
            token: response?.access_token,
            reportId,
            goldenDatasetId: goldenModelId,
            datasetId: targetModelId,
            reportWorkspaceId,
            datasetWorkspaceId: targetModelWorkspaceId
        });

        const embedConfig = {
            type: "report",
            id: reportId,
            embedUrl: embedResponse?.embedUrl,
            accessToken: tokenResponse?.token,
            tokenType: models.TokenType.Embed,
            permissions: models.Permissions.Read,
            // Dynamic binding for a thin report: same report id, different bound semantic
            // model per environment. Note the SINGULAR `datasetBinding` shape ({ datasetId }) —
            // this is a distinct property from the plural `datasetBindings` (paginated-report
            // rebinding), which is a silent no-op for a regular report. Confirmed against a
            // working reference implementation.
            datasetBinding: {
                datasetId: targetModelId
            },
            settings: {
                filterPaneEnabled: false,
                navContentPaneEnabled: false,
                layoutType: models.LayoutType.Custom,
                customLayout: {
                    displayOption: models.DisplayOption.FitToPage
                }
            }
        };

        yield put(actions.fetchDailyPowerBIReportSuccess(embedConfig));
    } catch (error) {
        console.error('Daily Report PowerBI API Error:', error);
        yield put(actions.fetchDailyPowerBIReportFailure('Failed to fetch PowerBI report'));
    }
}

function* fetchDailyReportLastRefreshedSaga() {
    try {
        const response = yield call(services.getDailyReportLastRefreshed);
        const lastRefreshedDateTime = response?.values?.LastRefreshedDateTime;
        const nextRefreshDateTime = response?.values?.NextRefreshDateTime ?? null;

        if (response?.success !== true || response?.values?.ExceptionId || !lastRefreshedDateTime) {
            throw new Error(response?.errorMessage || 'Failed to fetch Daily Matrix Report refresh date');
        }

        yield put(actions.fetchDailyReportLastRefreshedSuccess(lastRefreshedDateTime, nextRefreshDateTime));
    } catch (error) {
        console.error('Daily Matrix Report Last Refreshed API Error:', error);
        yield put(actions.fetchDailyReportLastRefreshedFailure('Failed to fetch refresh date'));
    }
}

export default function* dailyReportSaga() {
    yield takeEvery(types.FETCH_DAILY_POWER_BI_REPORT_REQUEST, fetchDailyPowerBIReportSaga);
    yield takeEvery(types.FETCH_DAILY_REPORT_LAST_REFRESHED_REQUEST, fetchDailyReportLastRefreshedSaga);
}
